import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import getAPIbaseURL from "../../../utils/helpers";
import { Modal, Button } from "react-bootstrap";
import { fetchRoles } from "../../../redux/slices/rolesSlice";
import { fetchManagers } from "../../../redux/slices/userSlice";
import useUserForm from "../useUserForm/useUserForm";
import UserForm from "../UserForm/UserForm";

const employmentTypes = [
  { value: "Full Time", label: "Full-time" },
  { value: "Part Time", label: "Part-time" },
  { value: "Contract", label: "Contract" },
  { value: "Intern", label: "Intern" },
  { value: "External", label: "External" },
];

const countries = [
  "Portugal", "Spain", "France", "Germany", "Italy", "United Kingdom", "United States", "Brazil", "Angola", "Mozambique", "Cape Verde", "Guinea-Bissau", "East Timor", "Luxembourg", "Belgium", "Switzerland", "Netherlands", "Ireland", "Canada", "Australia"
];

const UserDetailsModal = ({
  user,
  show,
  handleClose,
  delegations = [],
  onSave,
  startInEditMode = false,
}) => {
  const dispatch = useDispatch();
  const roles = useSelector((state) => state.roles.items);
  const [editMode, setEditMode] = useState(false);
  const [managers, setManagers] = useState([]);
  const roleOptions = roles.map((role) => ({ value: role.id, label: role.name }));

  const {
    form,
    setForm,
    errors,
    setErrors,
    validate,
    handleInputChange,
    isValid,
    touched
  } = useUserForm(user || {}, delegations, managers);

  useEffect(() => {
    setForm(user || {});
    setEditMode(startInEditMode);
    setErrors({});
  }, [user, show, startInEditMode, setForm, setErrors]);

  useEffect(() => {
    if (!show) return;
    dispatch(fetchRoles());
    dispatch(fetchManagers())
      .unwrap()
      .then((data) => setManagers(Array.isArray(data) ? data : []))
      .catch(() => setManagers([]));
  }, [dispatch, show]);

  const handleEdit = () => setEditMode(true);

  const handleCancelEdit = () => {
    setForm(user || {});
    setEditMode(false);
    setErrors({});
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    if (onSave) onSave(form);
    setEditMode(false);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${getAPIbaseURL()}/users/${form.id}/upload-photo`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formData,
      });
      if (!response.ok) {
        throw new Error("Failed to upload file");
      }
      const data = await response.json();
      handleInputChange({
        target: {
          name: "profile_picture",
          value: data.path,
          type: "text",
        },
      });
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  if (!form) return null;

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>User Details: {form.username}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit}>
          <UserForm
            form={form}
            errors={errors}
            editMode={editMode}
            delegations={delegations}
            managers={managers}
            roleOptions={roleOptions}
            employmentTypes={employmentTypes}
            countries={countries}
            handleInputChange={handleInputChange}
            handleFileChange={handleFileChange}
            touched={touched}
          />
          <div className="modal-footer">
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
            {!editMode ? (
              <Button variant="primary" onClick={handleEdit}>
                Edit
              </Button>
            ) : (
              <>
                <Button variant="outline-secondary" onClick={handleCancelEdit}>
                  Cancel Edit
                </Button>
                <Button type="submit" variant="success" disabled={!isValid}>
                  Save
                </Button>
              </>
            )}
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default UserDetailsModal;
